import type { SchuelerLeistungsdaten } from "@core/asd/data/schueler/SchuelerLeistungsdaten";
import type { SchuelerLernabschnittBemerkungen } from "@core/asd/data/schueler/SchuelerLernabschnittBemerkungen";
import type { SchuelerLernabschnittsdaten } from "@core/asd/data/schueler/SchuelerLernabschnittsdaten";
import type { FachDaten } from "@core/core/data/fach/FachDaten";
import type { GostSchuelerklausur } from "@core/core/data/gost/klausuren/GostSchuelerklausur";
import type { GostSchuelerklausurtermin } from "@core/core/data/gost/klausuren/GostSchuelerklausurtermin";
import type { JahrgangsDaten } from "@core/core/data/jahrgang/JahrgangsDaten";
import type { SchuelerLernabschnittListeEintrag } from "@core/core/data/schueler/SchuelerLernabschnittListeEintrag";
import type { FoerderschwerpunktEintrag } from "@core/core/data/schule/FoerderschwerpunktEintrag";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { GostHalbjahr } from "@core/core/types/gost/GostHalbjahr";
import { GostKlausurplanManager } from "@core/core/utils/gost/klausuren/GostKlausurplanManager";
import { ArrayList } from "@core/java/util/ArrayList";
import type { Collection } from "@core/java/util/Collection";
import type { List } from "@core/java/util/List";

import { SchuelerLernabschnittManager } from "~/components/schueler/lernabschnitte/SchuelerLernabschnittManager";
import { api } from "~/router/Api";
import { routeSchuelerLernabschnittLeistungen } from "~/router/apps/schueler/lernabschnitte/RouteSchuelerLernabschnittLeistungen";
import { RouteData, type RouteStateInterface } from "~/router/RouteData";
import { RouteManager } from "~/router/RouteManager";
import { RouteNode } from "~/router/RouteNode";
import { abschnittStateImpl } from "~/states/AbschnittStateImpl";
import { useSchuelerAuswahlState } from "~/states/schueler/SchuelerAuswahlState";
import { schuleStateImpl } from "~/states/SchuleStateImpl";

import { routeSchuelerLernabschnittGostKlausuren } from "./RouteSchuelerLernabschnittGostKlausuren";


interface RouteStateDataSchuelerLernabschnitte extends RouteStateInterface {
	// Daten, die in Abhängigkeit des ausgewählten Schülers geladen werden
	idSchueler: number;
	listAbschnitte: List<SchuelerLernabschnittListeEintrag>;
	hatGymOb: boolean;
	listFaecher: List<FachDaten>;
	listFoerderschwerpunkte: List<FoerderschwerpunktEintrag>;
	listJahrgaenge: List<JahrgangsDaten>;
	// Daten, die in Abhängigkeit des ausgewählten Lernabschnitts geladen werden
	auswahl: SchuelerLernabschnittListeEintrag | undefined;
	daten: SchuelerLernabschnittsdaten | undefined;
	manager: SchuelerLernabschnittManager | undefined;
	klausurManager: GostKlausurplanManager | undefined;
}

const defaultState = <RouteStateDataSchuelerLernabschnitte> {
	idSchueler: -1,
	listAbschnitte: new ArrayList<SchuelerLernabschnittListeEintrag>(),
	hatGymOb: false,
	listFaecher: new ArrayList(),
	listFoerderschwerpunkte: new ArrayList(),
	listJahrgaenge: new ArrayList(),
	auswahl: undefined,
	daten: undefined,
	manager: undefined,
	klausurManager: undefined,
	view: routeSchuelerLernabschnittLeistungen,
};


export class RouteDataSchuelerLernabschnitte extends RouteData<RouteStateDataSchuelerLernabschnitte> {

	public constructor() {
		super(defaultState);
	}

	get idSchueler(): number {
		return this._state.value.idSchueler;
	}

	get listAbschnitte(): List<SchuelerLernabschnittListeEintrag> {
		return this._state.value.listAbschnitte;
	}

	get hatGymOb(): boolean {
		return this._state.value.hatGymOb;
	}

	get manager(): SchuelerLernabschnittManager {
		if (this._state.value.manager === undefined) {
			throw new DeveloperNotificationException("Unerwarteter Fehler: Schüler-Lernabschnittsdaten nicht initialisiert");
		}
		return this._state.value.manager;
	}

	get hatKlausurManager(): boolean {
		return (this._state.value.klausurManager !== undefined);
	}

	get klausurManager(): GostKlausurplanManager {
		if (this._state.value.klausurManager === undefined) {
			throw new DeveloperNotificationException("Unerwarteter Fehler: Schüler-Klausurmanager nicht initialisiert");
		}
		return this._state.value.klausurManager;
	}

	protected getLernabschnitt(curState: RouteStateDataSchuelerLernabschnitte, idSchuljahresabschnitt: number, wechselNr: number): SchuelerLernabschnittListeEintrag | undefined {
		let found: SchuelerLernabschnittListeEintrag | undefined = undefined;
		for (const current of curState.listAbschnitte) {
			if (current.schuljahresabschnitt === idSchuljahresabschnitt) {
				found = current;
				if (current.wechselNr === wechselNr) {
					break;
				}
			}
		}
		return found;
	}

	protected async updateSchuljahresabschnitt(curState: RouteStateDataSchuelerLernabschnitte, newSchuljahresabschnitt: number | undefined, newWechselNr: number): Promise<RouteStateDataSchuelerLernabschnitte> {
		let found: SchuelerLernabschnittListeEintrag | undefined = undefined;
		// Prüfe, ob ein alter Schuljahresabschnitt (und ggf. die Wechselnummer) in der Liste der Lernabschnitte existiert
		if (newSchuljahresabschnitt !== undefined) {
			found = this.getLernabschnitt(curState, newSchuljahresabschnitt, newWechselNr);
		}
		// Wenn kein passender Lernabschnitt gefunden wurde, dann prüfe, ob ein Lernabschnitt für das aktuelle Halbjahr vorhanden ist.
		if (found === undefined) {
			const idSchuljahresabschnitt = abschnittStateImpl.auswahl.id;
			for (const current of curState.listAbschnitte) {
				if ((current.schuljahresabschnitt === idSchuljahresabschnitt) && (current.wechselNr === 0)) {
					found = current;
					break;
				}
			}
		}
		// Ansonsten nimm einfach den letzten Lernabschnitt der Liste
		if (found === undefined) {
			if (curState.listAbschnitte.isEmpty()) {
				return curState;
			}
			found = curState.listAbschnitte.get(curState.listAbschnitte.size() - 1);
		}
		const daten = await api.server.getSchuelerLernabschnittsdatenByID(api.schema, found.id);
		let listKurse;
		let listKlassen;
		let listLehrer;
		if ((this.hatAuswahl) && (found.schuljahresabschnitt === this.auswahl.schuljahresabschnitt)) {
			listKurse = this.manager.kursGetMenge();
			listKlassen = this.manager.klasseGetMenge();
			listLehrer = this.manager.lehrerGetMenge();
		} else {
			[listKurse, listKlassen, listLehrer] = await Promise.all([
				api.server.getKurseFuerAbschnitt(api.schema, found.schuljahresabschnitt),
				api.server.getListKlassenDatenBySchuljahresabschnitt(api.schema, found.schuljahresabschnitt),
				api.server.getLehrerFuerAbschnitt(api.schema, found.schuljahresabschnitt),
			]);
		}
		const schuelerAuswahlState = useSchuelerAuswahlState();
		const schueler = schuelerAuswahlState.manager.auswahl();
		const schuljahresabschnitt = abschnittStateImpl.getOrNull(daten.schuljahresabschnitt);
		if (schuljahresabschnitt === null) {
			throw new DeveloperNotificationException("Der Schülerlernabschnitt hat keinen gültigen Schuljahresabschnitt zugeordnet. Dies darf nicht vorkommen.");
		}
		const manager = new SchuelerLernabschnittManager(
			schuleStateImpl.schulform,
			schueler,
			daten,
			schuljahresabschnitt,
			curState.listFoerderschwerpunkte,
			curState.listJahrgaenge,
			listKlassen,
			listKurse,
			listLehrer
		);
		let klausurManager = undefined;
		const abiturjahrgang = schuelerAuswahlState.manager.auswahl().abiturjahrgang;
		if (routeSchuelerLernabschnittGostKlausuren.hatEineKompetenz() && abiturjahrgang !== null) {
			const halbjahr = GostHalbjahr.fromAbiturjahrSchuljahrUndHalbjahr(abiturjahrgang, found.schuljahr, found.abschnitt);
			if (halbjahr !== null) {
				const gostKlausurdaten = await api.server.getGostKlausurenKlausurdatenBySchuelerId(api.schema, schueler.id, abiturjahrgang, halbjahr.id);
				klausurManager = new GostKlausurplanManager(gostKlausurdaten.vorgaben, gostKlausurdaten.kursklausuren, gostKlausurdaten.termine, gostKlausurdaten.schuelerklausuren, gostKlausurdaten.schuelerklausurtermine);
				klausurManager.getKursManager().addAll(listKurse);
				for (const l of listLehrer) {
					klausurManager.getLehrerMap().put(l.id, l);
				}
			}
		}
		curState = Object.assign({ ... curState }, { auswahl: found, daten, manager, klausurManager });
		return curState;
	}


	/**
	 * Lädt die Informationen zu den Lernabschnitten anhand der übergebenen Schüler-ID
	 *
	 * @param idSchueler   die ID des Schülers
	 * @param force        gibt an, ob das Laden erzwungen werden soll, obwohl die ID bereits geladen ist
	 */
	public async setSchueler(idSchueler: number, force: boolean = false): Promise<void> {
		if ((!force) && (idSchueler === this._state.value.idSchueler)) {
			return;
		}
		const listAbschnitte = await api.server.getSchuelerLernabschnittsliste(api.schema, idSchueler);
		let hatGymOb: boolean = false;
		for (const abschnitt of listAbschnitte) {
			if (("EF" === abschnitt.jahrgang) || ("Q1" === abschnitt.jahrgang) || ("Q2" === abschnitt.jahrgang)) {
				hatGymOb = true;
				break;
			}
		}
		let listFoerderschwerpunkte;
		let listJahrgaenge;
		if (this.hatAuswahl) {
			listFoerderschwerpunkte = this.manager.foerderschwerpunktGetMenge();
			listJahrgaenge = this.manager.jahrgangGetMenge();
		} else {
			[listFoerderschwerpunkte, listJahrgaenge] = await Promise.all([
				api.server.getKatalogFoerderschwerpunkte(api.schema),
				api.server.getJahrgaenge(api.schema),
			]);
		}
		let newState = <RouteStateDataSchuelerLernabschnitte>{ idSchueler, listAbschnitte, hatGymOb, listFoerderschwerpunkte, listJahrgaenge, view: this._state.value.view };
		const alteAuswahl = this._state.value.auswahl;
		newState = await this.updateSchuljahresabschnitt(newState,
			alteAuswahl === undefined ? undefined : alteAuswahl.schuljahresabschnitt,
			alteAuswahl === undefined ? 0 : alteAuswahl.wechselNr);
		this.setPatchedDefaultState(newState);
	}

	get hatAuswahl(): boolean {
		return (this._state.value.auswahl !== undefined);
	}

	get auswahl(): SchuelerLernabschnittListeEintrag {
		if (this._state.value.auswahl === undefined) {
			throw new DeveloperNotificationException("Unerwarteter Fehler: Lernabschnittseintrag nicht festgelegt, es können keine Informationen zu den Leistungsdaten abgerufen oder eingegeben werden.");
		}
		return this._state.value.auswahl;
	}

	get daten(): SchuelerLernabschnittsdaten {
		if (this._state.value.daten === undefined) {
			throw new DeveloperNotificationException("Unerwarteter Fehler: Lernabschnittdaten nicht initialisiert");
		}
		return this._state.value.daten;
	}

	public async setLernabschnitt(idSchuljahresabschnitt: number, wechselNr: number) {
		const curAuswahl = this._state.value.auswahl;
		if ((curAuswahl === undefined) || ((idSchuljahresabschnitt === curAuswahl.schuljahresabschnitt) && (wechselNr === curAuswahl.wechselNr))) {
			return;
		}
		const newState = await this.updateSchuljahresabschnitt(this._state.value, idSchuljahresabschnitt, wechselNr);
		this.setPatchedState(newState);
	}

	gotoLernabschnitt = async (value: SchuelerLernabschnittListeEintrag) => {
		await RouteManager.doRoute(this.view.getRoute({ id: value.schuelerID, abschnitt: value.schuljahresabschnitt, wechselNr: value.wechselNr }));
	};

	patchLernabschnitt = async (data: Partial<SchuelerLernabschnittsdaten>) => {
		await api.server.patchSchuelerLernabschnittsdaten(data, api.schema, this.daten.id);
		Object.assign(this.daten, data);
		this.commit();
	};

	patchLeistung = async (data: Partial<SchuelerLeistungsdaten>, id: number) => {
		await api.server.patchSchuelerLeistungsdaten(data, api.schema, id);
		const leistung = this.manager.leistungGetByIdOrException(id);
		Object.assign(leistung, data);
		this.commit();
	};

	addLeistung = async (fachID: number) => {
		const data: Partial<SchuelerLeistungsdaten> = { lernabschnittID: this.daten.id, fachID };
		const result = await api.server.addSchuelerLeistungsdaten(data, api.schema);
		this.manager.leistungAdd(result);
		this.commit();
	};

	deleteLeistungen = async (leistungenIDs: Collection<number>) => {
		const leistungen = await api.server.deleteSchuelerLeistungsdatenMultiple(new ArrayList(leistungenIDs), api.schema);
		for (const l of leistungen) {
			this.manager.leistungRemoveByID(l.id);
		}
		this.commit();
	};

	patchBemerkungen = async (data: Partial<SchuelerLernabschnittBemerkungen>): Promise<void> => {
		await api.server.patchSchuelerLernabschnittsdatenBemerkungen(data, api.schema, this.daten.id);
		Object.assign(this.daten.bemerkungen, data);
		this.commit();
	};

	createSchuelerklausurtermin = async (skt: Partial<GostSchuelerklausurtermin>) => {
		delete skt.id;
		delete skt.folgeNr;
		const result = await api.server.createGostKlausurenSchuelerklausurtermin(skt, api.schema);
		// Dieser Manager enthält keine Raumdaten; die Raumlöschungen betreffen nur die vollständige Klausurplanung.
		this.klausurManager.schuelerklausurterminAddAll(result.schuelerklausurterminePatched);
		this.commit();
	};

	deleteSchuelerklausurtermin = async (skt: GostSchuelerklausurtermin) => {
		await api.server.deleteGostKlausurenSchuelerklausurtermin(api.schema, skt.id);
		this.klausurManager.schuelerklausurterminRemoveById(skt.id);
		this.commit();
	};

	patchSchuelerklausurtermin = async (id: number, skt: Partial<GostSchuelerklausurtermin>) => {
		const sktAlt = this.klausurManager.schuelerklausurterminGetByIdOrException(id);
		skt.id = id;
		await api.server.patchGostKlausurenSchuelerklausurtermin(skt, api.schema);
		this.klausurManager.schuelerklausurterminPatchAttributes(Object.assign(sktAlt, skt));
	};

	patchSchuelerklausur = async (id: number, sk: Partial<GostSchuelerklausur>) => {
		const skAlt = this.klausurManager.schuelerklausurGetByIdOrException(id);
		sk.id = id;
		await api.server.patchGostKlausurenSchuelerklausur(sk, api.schema);
		this.klausurManager.schuelerklausurPatchAttributes(Object.assign(skAlt, sk));
		this.commit();
	};

	gotoPlanung = async () => {
		const abiturjahr = this.manager.schuelerGet().abiturjahrgang;
		if (abiturjahr === null) {
			return;
		}
		const schuljahr = this.manager.schuljahresabschnittGet().schuljahr;
		const abschnitt = this.manager.schuljahresabschnittGet().abschnitt;
		const halbjahr = GostHalbjahr.fromAbiturjahrSchuljahrUndHalbjahr(abiturjahr, schuljahr, abschnitt);
		if (halbjahr === null) {
			return;
		}
		const dest = RouteNode.getNodeByName("gost.klausurplanung.nachschreiber")!.getRoute({ abiturjahr, halbjahr: halbjahr.id });
		await RouteManager.doRoute(dest);
	};

}
